import { createRouter, createWebHistory } from 'vue-router';
import { useMenuStore } from '@/store/modules/menu';
import { generatorDynamicRouter, sortMenuByOrder } from '@/utils/routerGenerator';

// 基础静态路由（登录、忘记密码等不需要认证的页面）
const basicRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/bigscreen',
    name: 'BigScreen',
    component: () => import('@/views/BigScreen.vue'),
    meta: { requiresAuth: true, title: '智慧大屏' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { requiresAuth: false }
  }
];

// 主布局路由（需要认证）
const mainLayoutRoute = {
  path: '/',
  name: 'MainLayout',
  component: () => import('@/layouts/MainLayout.vue'),
  meta: { requiresAuth: true },
  redirect: '/dashboard',
  children: [
    {
      path: 'dashboard',
      name: '数据概览',
      component: () => import('@/views/Dashboard.vue'),
      meta: { title: '数据概览', icon: 'DashboardOutlined', requiresAuth: true }
    },
    // 个人中心页面（静态路由）
    {
      path: 'profile',
      name: 'UserProfile',
      component: () => import('@/views/UserProfile.vue'),
      meta: { title: '个人中心', requiresAuth: true }
    },
    // 消息中心页面（静态路由）
    {
      path: 'message-center',
      name: 'MessageCenter',
      component: () => import('@/views/MessageCenter.vue'),
      meta: { title: '消息中心', requiresAuth: true }
    },
    // 动态菜单路由会插入到这里
  ]
};

const routes = [basicRoutes[0], basicRoutes[1], mainLayoutRoute];

// ... existing code ...
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

/**
 * 动态路由加载标志
 * 防止重复加载动态路由
 */
let dynamicRoutesLoaded = false;

/**
 * 为主布局添加动态路由
 * @param {Array} dynamicRoutes - 生成的动态路由数组
 */
function addDynamicRoutesToMainLayout(dynamicRoutes) {
  if (!dynamicRoutes || dynamicRoutes.length === 0) {
    // console.warn('[Router] 没有动态路由需要添加');
    return;
  }

  // console.log('[Router] 正在添加动态路由:', dynamicRoutes);

  // 过滤掉 404 路由（已在基础路由中）
  const filteredRoutes = dynamicRoutes.filter(route => route.name !== 'NotFound');
  
  console.log('[Router] 过滤掉 404 路由:', filteredRoutes);
  // 使用 router.addRoute 动态添加路由
  filteredRoutes.forEach(route => {
    // 添加为主布局（name 为 'MainLayout'）的子路由
    router.addRoute('MainLayout', route);
    // console.log('[Router] 已添加路由:', route.path, route.name);
  });
  
  // console.log(`✅ 已添加 ${filteredRoutes.length} 个动态路由`);
  // console.log('[Router] 当前所有路由:', router.getRoutes().map(r => ({ path: r.path, name: r.name })));
}

/**
 * 初始化动态路由
 * 从后端获取菜单数据并生成动态路由
 */
export async function initializeDynamicRoutes() {
  if (dynamicRoutesLoaded) {
    // console.log('[Router] 动态路由已加载，跳过重复加载');
    return true;
  }

  try {
    const menuStore = useMenuStore();
    
    // 获取菜单数据
    const menus = await menuStore.fetchMenus();
    
    if (!menus || menus.length === 0) {
      console.warn('[Router] 未获取到菜单数据');
      return false;
    }

    // 排序菜单
    const sortedMenus = sortMenuByOrder(menus);

    // 生成动态路由
    const dynamicRoutes = await generatorDynamicRouter(sortedMenus);

    // console.log('[Router] 动态路由已生成:', dynamicRoutes)

    // 为主布局添加动态路由
    addDynamicRoutesToMainLayout(dynamicRoutes);

    dynamicRoutesLoaded = true;
    return true;
  } catch (error) {
    console.error('[Router] 初始化动态路由失败:', error);
    return false;
  }
}

// 路由守卫
let dynamicRoutesInitialized = false;

/**
 * 路由守卫 - 检查授权及认证
 * 1. 检查授权令牌是否存在
 * 2. 未登录用户重定向到登录页
 * 3. 已登录用户首次需要加载动态路由
 */
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token');
  const publicRoutes = ['/login', '/forgot-password', '/mfa'];

  // console.log('[Router Guard] 导航到:', to.path, '来自:', from.path);
  // console.log('[Router Guard] 动态路由已初始化:', dynamicRoutesInitialized);

  // 如果是公开路由（不需要认证），直接放行
  if (publicRoutes.includes(to.path)) {
    // 如果深已登录且是登录页面，跳转到数据概览
    if (token && to.path === '/login') {
      next('/dashboard');
    } else {
      next();
    }
    return;
  }

  // 其他所有路由都需要认证
  if (!token) {
    // 未登录，跳转到登录页
    // console.log('[Router Guard] 未登录，重定向到登录页');
    next('/login');
    return;
  }

  // 已登录，检查是否需要加载动态路由
  if (!dynamicRoutesInitialized) {
    console.log('[Router Guard] 加载动态路由...');
    const success = await initializeDynamicRoutes();
    dynamicRoutesInitialized = true;

    if (success) {
      // 路由加载成功，重新导航到目标路由
      // console.log('[Router Guard] 动态路由加载成功，重新导航到:', to.fullPath);
      next({ ...to, replace: true });
    } else {
      // 加载失败，跳转到 dashboard
      // console.warn('[Router Guard] 动态路由加载失败，跳转到 dashboard');
      next('/dashboard');
    }
  } else {
    // 已经加载过动态路由，直接放行
    next();
  }
});

export default router;